import React, { useState, useMemo, useEffect } from 'react';
import { Pencil, Trash2, Eye, Plus, Search, X, Copy, Check } from 'lucide-react';
import DataTable from 'react-data-table-component';
import axios from "../axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initialMealPackages = [];

export default function MealPackage() {
    const [mealPackages, setMealPackages] = useState(initialMealPackages);
    const [mealPlan, setMealPlan] = useState([]);
    const [isCanvasOpen, setIsCanvasOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [portionDetails, setPortionDetails] = useState({
        portion: 0,
        weight: 0,
        servingSize: '',
        caloriesPerPortion: 0,
        proteinPerPortion: 0,
        fatPerPortion: 0,
        carbsPerPortion: 0
    });
    const [formErrors, setFormErrors] = useState({});
    const [isEditable, setIsEditable] = useState(false);
    const [mealData, setMealsData] = useState([]); // All meals from API (used for names)
    const [dateRange, setDateRange] = useState([]);
    const [showMealModal, setShowMealModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedMeals, setSelectedMeals] = useState({}); // { '2025-09-23': [ { _id, mealName, ... }, ... ] }
    const [selectedPackageGroups, setSelectedPackageGroups] = useState([]);
    const [copySourceDate, setCopySourceDate] = useState(null);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchMealPackages();
        fetchMeals();
        fetchProducts();
    }, []);

    // When selectedPackage dates change (user edit in the canvas), regenerate dateRange.
    useEffect(() => {
        if (selectedPackage?.startDate && selectedPackage?.endDate) {
            // pass current selectedMeals to preserve any selections user already made while adjusting dates
            generateDateRange(
                new Date(selectedPackage.startDate),
                new Date(selectedPackage.endDate),
                selectedMeals
            );
        } else {
            setDateRange([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPackage?.startDate, selectedPackage?.endDate]);

    // fetches
    const fetchMealPackages = async () => {
        try {
            const response = await axios.get("https://api.dailyfit.ae/api/admin/get-packages", { withCredentials: true });
            setMealPackages(response.data.data || []);
        } catch (error) {
            console.error("Error fetching meal packages:", error);
        }
    };

    const fetchMeals = async () => {
        try {
            const response = await axios.get("https://api.dailyfit.ae/api/admin/get-meals", { withCredentials: true });
            // Normalize to array if possible
            setMealsData(response.data.data?.meals || []);
        } catch (error) {
            console.error("Error fetching meals:", error);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get("https://api.dailyfit.ae/api/admin/get-meal-plans", { withCredentials: true });
            setMealPlan(response.data.data?.mealPlans || []);
        } catch (error) {
            console.error("Error fetching meal plans:", error);
        }
    };

    // Helper: normalize a meal item (could be id string, wrapped object, or full object)
    const normalizeMealObject = (m) => {
        if (!m) return null;
        if (typeof m === 'string') {
            const found = mealData.find(x => x._id === m);
            return found || { _id: m, mealName: 'Unknown Meal' };
        }
        // shape: { meal: {...} }
        if (m.meal && (m.meal._id || m.meal.mealName)) {
            return m.meal;
        }
        // already a meal object with _id
        if (m._id) return m;
        // fallback: return as-is
        return m;
    };

    /**
     * generateDateRange builds the dateRange array and initializes selectedMeals for each date.
     * If initialMealsFromPackage is provided (mapping of dateStr => array), it will merge/normalize it
     * so we don't overwrite selections when editing.
     */
    const generateDateRange = (startDate, endDate, initialMealsFromPackage = null) => {
        const dates = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        setDateRange(dates);

        // Build base map with empty arrays
        const initialSelectedMeals = {};
        dates.forEach(date => {
            const dateStr = date.toISOString().split('T')[0];
            initialSelectedMeals[dateStr] = [];
        });

        // If caller provided existing meals mapping, merge / normalize into initialSelectedMeals
        if (initialMealsFromPackage && typeof initialMealsFromPackage === 'object') {
            Object.entries(initialMealsFromPackage).forEach(([dateStr, mealsArr]) => {
                if (!dateStr) return;
                if (!Array.isArray(mealsArr)) return;
                const normalized = mealsArr.map(normalizeMealObject).filter(Boolean);
                // Only set for dateStrs that are inside the generated range; if not present, still allow
                initialSelectedMeals[dateStr] = normalized;
            });
        }

        setSelectedMeals(initialSelectedMeals);
    };

    const handleAdd = () => {
        setSelectedPackage({
            packageName: '',
            description: '',
            startDate: '',
            endDate: '',
            dailyMeals: {}
        });
        setIsCanvasOpen(true);
        setIsEditable(false);
        setFormErrors({});
        setDateRange([]);
        setSelectedMeals({});
        setSelectedPackageGroups([]);
    };

    const handleEdit = (mealPackage) => {
        console.group('=== handleEdit ===');
        console.log('Editing package:', mealPackage);
        setSelectedPackage(mealPackage);
        setIsCanvasOpen(true);
        setIsEditable(true);
        setFormErrors({});

        const packageGroups = Array.isArray(mealPackage.packageGroup) ? mealPackage.packageGroup :
            (mealPackage.packageGroup ? [mealPackage.packageGroup] : []);
        console.log('Setting package groups:', packageGroups);
        setSelectedPackageGroups(packageGroups);

        // Build mealsByDate map from various shapes API might send
        const mealsByDate = {};

        // If there's a .meals object where keys are day1, day2, ...
        if (mealPackage.meals && typeof mealPackage.meals === 'object' && !Array.isArray(mealPackage.meals)) {
            console.group('Processing package meals (object format)');
            Object.entries(mealPackage.meals).forEach(([dayKey, dayData]) => {
                if (dayData && dayData.date) {
                    const dateStr = dayData.date;
                    const rawMeals = Array.isArray(dayData.meals) ? dayData.meals : [];
                    mealsByDate[dateStr] = rawMeals.map(normalizeMealObject).filter(Boolean);
                } else {
                    console.warn(`Skipping invalid dayData for ${dayKey}:`, dayData);
                }
            });
            console.groupEnd();
        }
        // If .meals is an array (older format). Example: [ { day1: { date:'2025-09-23', meals: [...] } }, ... ]
        else if (mealPackage.meals && Array.isArray(mealPackage.meals)) {
            console.log('Processing package meals (array format):', mealPackage.meals);
            mealPackage.meals.forEach((dayMeal) => {
                const dayKey = Object.keys(dayMeal)[0];
                const dayData = dayMeal[dayKey];
                if (dayData && dayData.date) {
                    const dateStr = dayData.date;
                    const rawMeals = Array.isArray(dayData.meals) ? dayData.meals : [];
                    mealsByDate[dateStr] = rawMeals.map(normalizeMealObject).filter(Boolean);
                }
            });
        }
        // If API provided a mealsByDate field directly
        else if (mealPackage.mealsByDate && typeof mealPackage.mealsByDate === 'object') {
            Object.entries(mealPackage.mealsByDate).forEach(([key, dayData]) => {
                if (dayData && dayData.date) {
                    const dateStr = dayData.date;
                    const rawMeals = Array.isArray(dayData.meals) ? dayData.meals : [];
                    mealsByDate[dateStr] = rawMeals.map(normalizeMealObject).filter(Boolean);
                }
            });
        }
        // Some packages might include dailyMeals directly
        else if (mealPackage.dailyMeals && typeof mealPackage.dailyMeals === 'object') {
            Object.entries(mealPackage.dailyMeals).forEach(([dateStr, mealsArr]) => {
                const rawMeals = Array.isArray(mealsArr) ? mealsArr : [];
                mealsByDate[dateStr] = rawMeals.map(normalizeMealObject).filter(Boolean);
            });
        } else {
            console.log('No valid meals data found in package');
        }

        console.log('Final mealsByDate computed from package:', mealsByDate);

        // Set portion details if they exist
        if (mealPackage.portionDetails) {
            setPortionDetails({
                portion: mealPackage.portionDetails.portion || 0,
                weight: mealPackage.portionDetails.weight || 0,
                servingSize: mealPackage.portionDetails.servingSize || '',
                caloriesPerPortion: mealPackage.portionDetails.caloriesPerPortion || 0,
                proteinPerPortion: mealPackage.portionDetails.proteinPerPortion || 0,
                fatPerPortion: mealPackage.portionDetails.fatPerPortion || 0,
                carbsPerPortion: mealPackage.portionDetails.carbsPerPortion || 0
            });
        } else {
            // Reset to default values if no portion details exist
            setPortionDetails({
                portion: 0,
                weight: 0,
                servingSize: '',
                caloriesPerPortion: 0,
                proteinPerPortion: 0,
                fatPerPortion: 0,
                carbsPerPortion: 0
            });
        }

        // Instead of setting selectedMeals directly (and risk being overwritten by a separate dateRange init),
        // call generateDateRange and pass mealsByDate as initialMealsFromPackage so generateDateRange initializes properly.
        if (mealPackage.startDate && mealPackage.endDate) {
            const start = new Date(mealPackage.startDate);
            const end = new Date(mealPackage.endDate);
            generateDateRange(start, end, mealsByDate);
        } else {
            // If no dates are present, still set the selectedMeals mapping (for safety)
            setSelectedMeals(mealsByDate);
        }

        console.groupEnd();
    };

    const validateForm = (formData) => {
        const errors = {};
        if (!formData.get('packageName')) errors.packageName = "Package name is required.";
        if (!formData.get('startDate')) errors.startDate = "Start date is required.";
        if (!formData.get('endDate')) errors.endDate = "End date is required.";
        if (!selectedPackageGroups || selectedPackageGroups.length === 0) errors.packageGroup = "At least one package group is required.";

        // Check if end date is after start date
        const startDate = new Date(formData.get('startDate'));
        const endDate = new Date(formData.get('endDate'));
        if (startDate > endDate) {
            errors.endDate = "End date must be after start date.";
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData(e.target);
        const errors = validateForm(formData);

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setLoading(false);
            return;
        }

        const startDate = new Date(formData.get("startDate"));
        const endDate = new Date(formData.get("endDate"));

        // Convert selectedMeals to the required format for API (use IDs)
        const mealsArray = [];
        let dayCounter = 1;
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            const dateStr = currentDate.toISOString().split('T')[0];

            // map to IDs (safe for API)
            const mealsForDay = (selectedMeals[dateStr] || []).map(m => {
                if (!m) return null;
                if (typeof m === 'string') return m;
                return m._id || (m.meal && m.meal._id) || null;
            }).filter(Boolean);

            mealsArray.push({
                [`day${dayCounter}`]: {
                    meals: mealsForDay,
                    date: dateStr,
                },
            });

            currentDate.setDate(currentDate.getDate() + 1);
            dayCounter++;
        }

        const packageIdentifier = selectedPackage?.identifier ||
            `${formData.get("packageName").toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

        const packageData = {
            packageName: formData.get("packageName"),
            description: formData.get("description"),
            packageGroup: selectedPackageGroups,
            startDate: formData.get("startDate"),
            endDate: formData.get("endDate"),
            identifier: packageIdentifier,
            meals: mealsArray,
            isDeleted: false,
            isPermanent: false,
            repeatTillEnd: true,
            portionDetails: {
                portion: portionDetails.portion,
                weight: portionDetails.weight,
                servingSize: portionDetails.servingSize,
                caloriesPerPortion: portionDetails.caloriesPerPortion,
                proteinPerPortion: portionDetails.proteinPerPortion,
                fatPerPortion: portionDetails.fatPerPortion,
                carbsPerPortion: portionDetails.carbsPerPortion
            }
        };

        try {
            let response;
            if (isEditable && selectedPackage) {
                response = await axios.patch(
                    "https://api.dailyfit.ae/api/admin/update-package",
                    packageData,
                    { withCredentials: true }
                );
            } else {
                response = await axios.post(
                    "https://api.dailyfit.ae/api/admin/add-package",
                    packageData,
                    { withCredentials: true }
                );
            }

            toast.success(response?.data?.message || "Package saved successfully!");
            await fetchMealPackages();
            setIsCanvasOpen(false);
        } catch (error) {
            console.error("Error saving meal package:", error);
            toast.error(error?.response?.data?.message || "Failed to save the meal package.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`https://api.dailyfit.ae/api/admin/delete`, {
                data: { identifier: id },
                withCredentials: true,
            });
            toast.success("Package deleted successfully!");
            fetchMealPackages();
        } catch (error) {
            console.error("Error deleting package:", error);
            toast.error("Failed to delete package. Please try again.");
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setSelectedPackage(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const openMealSelectionModal = (date) => {
        setSelectedDate(date);
        setShowMealModal(true);
    };

    const handleMealSelection = (meal) => {
        if (!selectedDate) return;
        const dateStr = selectedDate.toISOString().split('T')[0];
        setSelectedMeals(prev => {
            const currentMeals = [...(prev[dateStr] || [])];
            const index = currentMeals.findIndex(m => (m && m._id) === meal._id);

            if (index === -1) {
                // Add meal object (normalize)
                return {
                    ...prev,
                    [dateStr]: [...currentMeals, normalizeMealObject(meal)]
                };
            } else {
                // Remove meal
                currentMeals.splice(index, 1);
                return {
                    ...prev,
                    [dateStr]: currentMeals
                };
            }
        });
    };

    const getMealObjectsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        const meals = selectedMeals[dateStr] || [];
        return meals.map(meal => {
            if (!meal) return null;
            if (typeof meal === 'string') return { _id: meal, mealName: 'Unknown Meal' };
            return meal.meal || meal;
        }).filter(Boolean);
    };

    const getMealNamesForDate = (date) => {
        return getMealObjectsForDate(date).map(meal => getMealName(meal));
    };

    const removeMealFromDate = (date, mealId) => {
        if (!date || !mealId) return;
        const dateStr = date.toISOString().split('T')[0];
        setSelectedMeals(prev => ({
            ...prev,
            [dateStr]: (prev[dateStr] || []).filter(meal => {
                if (!meal) return false;
                const id = typeof meal === 'string' ? meal : (meal._id || '');
                return id !== mealId;
            })
        }));
        toast.success('Meal removed successfully');
    };

    const getMealCountForDate = (date) => {
        const dateStr = date.toISOString().split('T')[0];
        return selectedMeals[dateStr]?.length || 0;
    };

    const openCopyModal = (sourceDate) => {
        setCopySourceDate(sourceDate);
        setShowCopyModal(true);
    };

    const handleCopyMeals = (targetDate) => {
        if (!copySourceDate) return;
        const sourceDateStr = copySourceDate.toISOString().split('T')[0];
        const targetDateStr = targetDate.toISOString().split('T')[0];

        // Copy meal objects
        setSelectedMeals(prev => ({
            ...prev,
            [targetDateStr]: [...(prev[sourceDateStr] || []).map(normalizeMealObject)]
        }));
        toast.success(`Meals copied to ${targetDate.toLocaleDateString()}`);
    };

    const copyToAllDays = () => {
        if (!copySourceDate) return;

        const sourceDateStr = copySourceDate.toISOString().split('T')[0];
        const sourceMeals = (selectedMeals[sourceDateStr] || []).map(normalizeMealObject);

        // Copy to all dates except source
        const updatedMeals = { ...selectedMeals };
        dateRange.forEach(date => {
            const dateStr = date.toISOString().split('T')[0];
            if (dateStr !== sourceDateStr) {
                updatedMeals[dateStr] = [...sourceMeals];
            }
        });

        setSelectedMeals(updatedMeals);
        setShowCopyModal(false);
        toast.success("Meals copied to all days!");
    };

    const isMealSelected = (mealId) => {
        if (!mealId) return false;
        
        // If we have a selected date, only check that date
        if (selectedDate) {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const meals = selectedMeals[dateStr] || [];
            return meals.some(meal => {
                if (!meal) return false;
                const id = typeof meal === 'string' ? meal : (meal._id || '');
                return id === mealId;
            });
        }
        
        // If no selected date (like in the meal selection modal), check all dates
        return Object.values(selectedMeals).some(meals => {
            return Array.isArray(meals) && meals.some(meal => {
                if (!meal) return false;
                const id = typeof meal === 'string' ? meal : (meal._id || '');
                return id === mealId;
            });
        });
    };

    const filteredPackages = useMemo(() => {
        // First filter by search term
        const filtered = mealPackages.filter((pkg) =>
            (pkg.packageName?.toLowerCase() || "").includes(searchTerm.toLowerCase())
        );

        // Group by package groups
        const groupedPackages = {};

        // First, create all group entries
        const allGroups = new Set();
        filtered.forEach(pkg => {
            const groups = Array.isArray(pkg.packageGroup) ? pkg.packageGroup :
                (pkg.packageGroup ? [pkg.packageGroup] : ['Uncategorized']);
            groups.forEach(group => allGroups.add(group));
        });

        // Initialize all groups with empty arrays
        allGroups.forEach(group => {
            groupedPackages[group] = [];
        });

        // Then assign packages to their groups
        filtered.forEach(pkg => {
            const groups = Array.isArray(pkg.packageGroup) ? pkg.packageGroup :
                (pkg.packageGroup ? [pkg.packageGroup] : ['Uncategorized']);

            groups.forEach(group => {
                if (groupedPackages[group]) {
                    groupedPackages[group].push(pkg);
                }
            });
        });

        return groupedPackages;
    }, [mealPackages, searchTerm]);

    const getMealName = (mealIdOrObj) => {
        if (!mealIdOrObj) return "Unknown Meal";
        
        let meal = mealIdOrObj;
        // if argument is a string ID, look up the meal object
        if (typeof mealIdOrObj === 'string') {
            meal = mealData.find(m => m._id === mealIdOrObj);
            if (!meal) return "Unknown Meal";
        }
        
        // Get the base meal name
        const mealName = meal.mealName || meal.name || "Unknown Meal";
        
        // Check if we have portion details to append
        if (meal.portionDetails) {
            const { portion, servingSize } = meal.portionDetails;
            let portionText = [];
            
            if (portion) portionText.push(`${portion} ${portion === 1 ? 'portion' : 'portions'}`);
            if (servingSize) portionText.push(servingSize);
            
            if (portionText.length > 0) {
                return `${mealName} (${portionText.join(' - ')})`;
            }
        }
        
        return mealName;
    };

    const columns = [
        { name: 'Name', selector: (row) => row.packageName, sortable: true },
        { name: 'Start Date', selector: (row) => new Date(row.startDate).toLocaleDateString(), sortable: true },
        { name: 'End Date', selector: (row) => new Date(row.endDate).toLocaleDateString(), sortable: true },
        {
            name: 'Duration',
            selector: (row) => {
                const start = new Date(row.startDate);
                const end = new Date(row.endDate);
                const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
                return `${days} day${days !== 1 ? 's' : ''}`;
            },
            sortable: true
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="flex justify-center space-x-2">
                    <button onClick={() => handleEdit(row)} className="bg-yellow-500 text-white p-2 rounded hover:bg-yellow-600">
                        <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDelete(row.identifier)} className="bg-red-500 text-white p-2 rounded hover:bg-red-600">
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="p-4">
            {loading && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
                    <div className="text-white text-xl font-semibold">Loading...</div>
                </div>
            )}
            <div className="flex justify-end mb-4">
                <button onClick={handleAdd} className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">
                    <Plus size={16} />
                    <span>Add Meal Package</span>
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md">
                <div className="p-4 border-b">
                    <div className="flex justify-end items-center space-x-4">
                        <div className="relative w-64">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search meal package..."
                                className="pl-8 pr-4 py-2 w-full border rounded-md"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    {Object.entries(filteredPackages).length > 0 ? (
                        Object.entries(filteredPackages).map(([group, packages]) => (
                            packages.length > 0 && (
                                <div key={group} className="mb-8">
                                    <h3 className="text-lg font-semibold mb-2">{group}</h3>
                                    <DataTable
                                        columns={columns}
                                        data={packages}
                                        pagination
                                        responsive
                                        highlightOnHover
                                        noDataComponent={`No packages found in ${group}`}
                                        className="mb-6"
                                    />
                                </div>
                            )
                        ))
                    ) : (
                        <div className="text-center py-4 text-gray-500">
                            No meal packages found
                        </div>
                    )}
                </div>
            </div>

            {/* Main Canvas Form */}
            {isCanvasOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-40">
                    <div className="bg-white w-1/3 p-6 h-full overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{isEditable ? 'Edit Meal Package' : 'Add Meal Package'}</h2>
                            <button onClick={() => setIsCanvasOpen(false)} className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600">
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block mb-1">Package Name:</label>
                                <input
                                    name="packageName"
                                    defaultValue={selectedPackage?.packageName}
                                    className="w-full border p-2 rounded"
                                    required
                                />
                                {formErrors.packageName && <p className="text-red-500 text-sm">{formErrors.packageName}</p>}
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1">Description:</label>
                                <textarea
                                    name="description"
                                    defaultValue={selectedPackage?.description}
                                    className="w-full border p-2 rounded h-24"
                                />
                            </div>

                            <div className="mb-4">
                                <label htmlFor="packageGroup" className="block mb-1">
                                    Select Package Group
                                </label>
                                <div className="space-y-2">
                                    {['Full Package', 'Main Meals with Breakfast', 'Main Meals with FITT Snacks', 'Main Meals Only'].map((group) => (
                                        <div key={group} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`group-${group}`}
                                                checked={selectedPackageGroups.includes(group)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedPackageGroups(prev => [...prev, group]);
                                                    } else {
                                                        setSelectedPackageGroups(prev => prev.filter(g => g !== group));
                                                    }
                                                }}
                                                className="h-4 w-4 text-blue-600 rounded"
                                            />
                                            <label htmlFor={`group-${group}`} className="ml-2 text-gray-700">
                                                {group}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {formErrors.packageGroup && <p className="text-red-500 text-sm">{formErrors.packageGroup}</p>}
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1">Start Date:</label>
                                <input
                                    name="startDate"
                                    type="date"
                                    value={selectedPackage?.startDate ? new Date(selectedPackage.startDate).toISOString().split('T')[0] : ""}
                                    onChange={handleDateChange}
                                    className="w-full border p-2 rounded"
                                    required
                                />
                                {formErrors.startDate && <p className="text-red-500 text-sm">{formErrors.startDate}</p>}
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1">End Date:</label>
                                <input
                                    name="endDate"
                                    type="date"
                                    value={selectedPackage?.endDate ? new Date(selectedPackage.endDate).toISOString().split('T')[0] : ""}
                                    onChange={handleDateChange}
                                    className="w-full border p-2 rounded"
                                    required
                                />
                                {formErrors.endDate && <p className="text-red-500 text-sm">{formErrors.endDate}</p>}
                            </div>

                            {/* Portion Details Section */}
                            <div className="mb-6 border-t pt-4">
                                <h3 className="text-lg font-semibold mb-3">Portion Details</h3>
                                
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block mb-1">Portion (count):</label>
                                        <input
                                            type="number"
                                            value={portionDetails.portion}
                                            onChange={(e) => setPortionDetails(prev => ({ ...prev, portion: parseInt(e.target.value) || 0 }))}
                                            className="w-full border p-2 rounded"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1">Weight (grams):</label>
                                        <input
                                            type="number"
                                            value={portionDetails.weight}
                                            onChange={(e) => setPortionDetails(prev => ({ ...prev, weight: parseInt(e.target.value) || 0 }))}
                                            className="w-full border p-2 rounded"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block mb-1">Serving Size:</label>
                                    <input
                                        type="text"
                                        value={portionDetails.servingSize}
                                        onChange={(e) => setPortionDetails(prev => ({ ...prev, servingSize: e.target.value }))}
                                        className="w-full border p-2 rounded"
                                        placeholder="e.g., 7 days x 3 meals"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block mb-1">Calories per Portion:</label>
                                        <input
                                            type="number"
                                            value={portionDetails.caloriesPerPortion}
                                            onChange={(e) => setPortionDetails(prev => ({ ...prev, caloriesPerPortion: parseInt(e.target.value) || 0 }))}
                                            className="w-full border p-2 rounded"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1">Protein (g) per Portion:</label>
                                        <input
                                            type="number"
                                            value={portionDetails.proteinPerPortion}
                                            onChange={(e) => setPortionDetails(prev => ({ ...prev, proteinPerPortion: parseInt(e.target.value) || 0 }))}
                                            className="w-full border p-2 rounded"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1">Fat (g) per Portion:</label>
                                        <input
                                            type="number"
                                            value={portionDetails.fatPerPortion}
                                            onChange={(e) => setPortionDetails(prev => ({ ...prev, fatPerPortion: parseInt(e.target.value) || 0 }))}
                                            className="w-full border p-2 rounded"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1">Carbs (g) per Portion:</label>
                                        <input
                                            type="number"
                                            value={portionDetails.carbsPerPortion}
                                            onChange={(e) => setPortionDetails(prev => ({ ...prev, carbsPerPortion: parseInt(e.target.value) || 0 }))}
                                            className="w-full border p-2 rounded"
                                            min="0"
                                            step="0.1"
                                        />
                                    </div>
                                </div>
                            </div>

                            {dateRange.length > 0 && (
                                <div className="mb-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="font-medium">Meal Schedule:</label>
                                    </div>

                                    <div className="border rounded-lg overflow-hidden">
                                        <div className="grid grid-cols-1 divide-y">
                                            {dateRange.map((date) => {
                                                const dateKey = date.toISOString().split('T')[0];
                                                return (
                                                    <div key={dateKey} className="p-4 hover:bg-gray-50 flex justify-between items-center">
                                                        <div>
                                                            <p className="font-medium">{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                                                            <div className="text-sm text-gray-500">
                                                                {getMealCountForDate(date) > 0 ? (
                                                                    <div className="space-y-1">
                                                                        {getMealObjectsForDate(date).map((meal, i) => (
                                                                            <div key={i} className="flex items-center group">
                                                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></span>
                                                                                <span>{getMealName(meal)}</span>
                                                                                <button 
                                                                                    type="button"
                                                                                    className="ml-2 text-red-500 hover:text-red-700 hover:bg-red-100 p-1 rounded-full transition-colors"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (window.confirm('Are you sure you want to remove this meal?')) {
                                                                                            removeMealFromDate(date, meal._id);
                                                                                        }
                                                                                    }}
                                                                                    title="Remove meal"
                                                                                >
                                                                                    <X size={14} />
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <span>No meals selected</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                type="button"
                                                                className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                                                onClick={() => openMealSelectionModal(date)}
                                                            >
                                                                <Eye size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="p-2 bg-green-500 text-white rounded hover:bg-green-600"
                                                                onClick={() => openCopyModal(date)}
                                                            >
                                                                <Copy size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end space-x-4 p-4 bg-white shadow-inner rounded-b-lg">
                                <button type="submit" className="w-32 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition duration-300 text-lg">
                                    {isEditable ? 'Update' : 'Save'}
                                </button>
                                <button type="button" onClick={() => setIsCanvasOpen(false)} className="w-32 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition duration-300 text-lg">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Meal Selection Modal */}
            {showMealModal && selectedDate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg w-1/2 max-h-3/4 overflow-hidden">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold">
                                Select Meals for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            </h3>
                            <button onClick={() => setShowMealModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4 max-h-96 overflow-y-auto">
                            <div className="grid grid-cols-1 gap-2">
                                {mealData && mealData.length > 0 ? (
                                    mealData.map((meal) => {
                                        const selected = isMealSelected(meal._id);
                                        return (
                                            <div
                                                key={meal._id}
                                                className={`flex items-center p-3 rounded-lg border ${selected ? 'bg-blue-50 border-blue-400' : 'hover:bg-gray-50'}`}
                                                onClick={() => handleMealSelection(meal)}
                                            >
                                                <div className={`w-6 h-6 mr-3 flex items-center justify-center rounded-full ${selected ? 'bg-blue-500 text-white' : 'border border-gray-300'}`}>
                                                    {selected && <Check size={14} />}
                                                </div>
                                                <div>
                                                    <p className="font-medium">{getMealName(meal)}</p>
                                                    <p>
                                                        {Array.isArray(meal.mealTypeDetails) ? (
                                                            meal.mealTypeDetails.map((item, index) => (
                                                                <span key={index}>{item.mealType} </span>
                                                            ))
                                                        ) : null}
                                                    </p>
                                                    {meal.description && <p className="text-sm text-gray-500">{meal.description}</p>}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-center py-4">No meals available</p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end p-4 border-t">
                            <button
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={() => setShowMealModal(false)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Copy Meals Modal */}
            {showCopyModal && copySourceDate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg w-1/2 max-h-3/4 overflow-hidden">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-semibold">
                                Copy Meals from {copySourceDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </h3>
                            <button onClick={() => setShowCopyModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="mb-4">
                                <h4 className="font-medium mb-2">Selected meals to copy:</h4>
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    {copySourceDate && (
                                        <div>
                                            {selectedMeals[copySourceDate.toISOString().split('T')[0]]?.length > 0 ? (
                                                <ul className="list-disc pl-5">
                                                    {selectedMeals[copySourceDate.toISOString().split('T')[0]].map((meal) => {
                                                        const mealObj = normalizeMealObject(meal);
                                                        return <li key={mealObj._id || JSON.stringify(meal)}>{getMealName(mealObj)}</li>;
                                                    })}
                                                </ul>
                                            ) : (
                                                <p className="text-gray-500">No meals selected for this day</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-medium">Select target date(s):</h4>
                                    <button
                                        className="text-sm text-blue-500 hover:text-blue-700"
                                        onClick={copyToAllDays}
                                    >
                                        Copy to all days
                                    </button>
                                </div>

                                <div className="max-h-64 overflow-y-auto border rounded-lg">
                                    <div className="grid grid-cols-1 divide-y">
                                        {dateRange.map((date) => {
                                            // Don't show source date as a target option
                                            if (date.toISOString().split('T')[0] === copySourceDate.toISOString().split('T')[0]) {
                                                return null;
                                            }

                                            const key = date.toISOString().split('T')[0];
                                            return (
                                                <div key={key} className="p-3 hover:bg-gray-50 flex justify-between items-center">
                                                    <p>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                                                        onClick={() => handleCopyMeals(date)}
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end p-4 border-t">
                            <button
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                onClick={() => setShowCopyModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" />
        </div>
    );
}
